import React, { useCallback, useState } from 'react';
import {
  ThemeProvider,
  Tooltip,
  Button,
  Menu,
  MenuItemGroup,
  MenuButtonTrigger,
} from '@amzn/storm-ui';
import { ActionDropdownGroup, POSITION } from '../types';
import { MenuItemLink } from './styles';

const SingleLinkButton: React.VFC<{
  group: ActionDropdownGroup;
  isDisabled: boolean;
}> = ({ group, isDisabled }) => {
  const id = group.id;
  const link = group.links[0];
  const label = link.label ?? group.label;
  return (
    <Button
      id={id}
      data-eventid={id}
      data-ccx-e2e-id={`aac-button-${id}`}
      disabled={isDisabled}
      onClick={() => {
        window.location.assign(link.url);
      }}
      primary={false}
      aria-label={label}
    >
      {label}
    </Button>
  );
};

export const ActionsDropdown: React.VFC<{
  id: string;
  groups?: ActionDropdownGroup[];
  label: string;
  position?: POSITION;
  selectedValue?: string;
  state: string;
  toolTip?: string;
  tooltipPosition?: POSITION;
  ariaLabel?: string;
}> = ({
  id,
  groups,
  label,
  position,
  toolTip,
  tooltipPosition,
  state,
  ariaLabel,
}) => {
  const [active, setActive] = useState(false);
  const isDisabled = state === 'DISABLED';
  const singleLinkDropdown =
    groups?.length === 1 && groups[0].links?.length === 1;

  const onClick = useCallback(() => {
    if (!isDisabled) {
      setActive(!active);
    }
  }, [active, setActive]);

  const onClose = useCallback(() => {
    setActive(false);
  }, [setActive]);

  const menuElement = singleLinkDropdown ? (
    <SingleLinkButton group={groups[0]} isDisabled={isDisabled} />
  ) : (
    <Menu
      id={id}
      isOpen={active}
      onClick={onClick}
      onClose={onClose}
      padding="none"
      position={position}
      spacing={0}
      disabled={isDisabled}
      trigger={
        <MenuButtonTrigger disabled={isDisabled} aria-label={ariaLabel}>
          {label}
        </MenuButtonTrigger>
      }
      withArrow={false}
    >
      {groups?.map((group) => (
        <MenuItemGroup key={group.id}>
          {group.links?.map((link) => (
            <MenuItemLink
              key={link.id}
              id={link.id}
              href={link.url}
              onClick={link.onClickCallback}
              disabled={link.isDisabled}
              target="_self"
            >
              {link.label}
            </MenuItemLink>
          ))}
        </MenuItemGroup>
      ))}
    </Menu>
  );

  return (
    <ThemeProvider
      theme={(theme) => {
        return {
          ...theme,
          popover: {
            ...theme?.popover,
            zIndex: 502,
          },
        };
      }}
    >
      {toolTip?.length ? (
        <Tooltip
          trigger={<div>{menuElement}</div>}
          message={toolTip}
          type={isDisabled ? 'dark' : 'light'}
          position={tooltipPosition}
          withCloseButton={false}
        />
      ) : (
        menuElement
      )}
    </ThemeProvider>
  );
};
